/**
 * A failed job's reason on the Files page (R1 of docs/plans/2026-10-19_reader-layout-change.md): the diagnosis in plain
 * words when the runner sent one, the column, the value and what the reader expected, and for a platform admin the
 * technical part (the raw error, the reader's own line) under Details. A job with no diagnosis shows its error as before.
 * When the failure opened or joined a revision of its reader (R2), where that update stands comes first.
 */
import type { ImportJob } from "@/lib/api/imports";
import { diagnosisWords, revisionWords } from "@/lib/imports/diagnosis";

export function JobFailureView({ job, isPlatformAdmin }: { job: ImportJob; isPlatformAdmin: boolean }) {
  const words = job.diagnosis ? diagnosisWords(job.diagnosis) : { plain: [], admin: [] };
  const update = job.status === "failed" ? revisionWords(job.revision_status) : "";
  const updateLine = update && (
    <p className="text-on-surface" data-revision={job.revision_status}>
      {update}
    </p>
  );
  if (words.plain.length === 0) {
    return (
      <div className="mt-1 max-w-md space-y-0.5 text-xs">
        {updateLine}
        <div className="text-danger">{job.error}</div>
      </div>
    );
  }
  const details = [job.error ?? "", ...words.admin.filter((line) => !(job.error ?? "").includes(line))].filter(Boolean);
  return (
    <div className="mt-1 max-w-md space-y-0.5 text-xs" data-diagnosis="">
      {updateLine}
      {words.plain.map((line) => (
        <p key={line} className="text-danger">
          {line}
        </p>
      ))}
      {isPlatformAdmin && details.length > 0 && (
        <details className="text-on-surface-muted">
          <summary className="cursor-pointer">Details</summary>
          {details.map((line) => (
            <p key={line} className="break-words font-mono text-[11px]">
              {line}
            </p>
          ))}
        </details>
      )}
    </div>
  );
}
